// Stock initial (reprise d'inventaire) : stock existant avant l'utilisation de la plateforme, par type de poteau.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { stockAPI, stockInitialAPI } from '../services/api';
import { apiError, dateFr, dateHeureFr } from '../utils/format';
import { tokens } from '../theme';

const chiffres = (v) => String(v ?? '').replace(/[^\d]/g, '');
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const StockInitialDialog = ({ open, onClose, onSaved }) => {
  const [onglet, setOnglet] = useState(0);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState({});
  const [date, setDate] = useState(aujourdhui());
  const [dateProd, setDateProd] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [hist, setHist] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const charger = () => stockInitialAPI.list().then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => {
    if (!open) return;
    setError(''); setQ({}); setDate(aujourdhui()); setDateProd(''); setCommentaire(''); setOnglet(0);
    stockAPI.getPbaTypes().then((r) => setTypes(r.data)).catch((e) => setError(apiError(e)));
    charger();
  }, [open]);

  const set = (code, k, v) => setQ((x) => ({ ...x, [code]: { ...x[code], [k]: chiffres(v) } }));
  const n = (code, k) => Number(q[code]?.[k] || 0);
  const totMature = types.reduce((s, t) => s + n(t.code, 'mature'), 0);
  const totMat = types.reduce((s, t) => s + n(t.code, 'en_maturation'), 0);

  const enregistrer = async () => {
    setBusy(true); setError('');
    try {
      const lignes = types.map((t) => ({ pba_type: t.code, mature: n(t.code, 'mature'), en_maturation: n(t.code, 'en_maturation') })).filter((l) => l.mature || l.en_maturation);
      const r = await stockInitialAPI.create({ date, date_production: dateProd || undefined, commentaire, lignes });
      onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const annuler = async (h) => {
    if (!window.confirm(`Annuler la reprise du ${dateFr(h.date)} (${h.total} poteaux) ?`)) return;
    try { await stockInitialAPI.remove(h.reference); charger(); onSaved('Reprise de stock initial annulée', true); } catch (e) { setError(apiError(e)); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Stock initial — reprise d'inventaire</DialogTitle>
      <Tabs value={onglet} onChange={(e, v) => setOnglet(v)} sx={{ px: 3 }}>
        <Tab label="Saisir" /><Tab label={`Reprises enregistrées${hist.length ? ` (${hist.length})` : ''}`} />
      </Tabs>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {onglet === 0 ? (
          <>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
              Poteaux présents dans le parc avant l'utilisation de l'ERP (ou trouvés lors d'un inventaire). Ils s'ajoutent au stock sans compter comme production.
              Les <b>livrables</b> sont matures dès la date d'inventaire ; ceux <b>en maturation</b> deviennent livrables selon leur date de production.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Date de l'inventaire" InputLabelProps={{ shrink: true }}
                inputProps={{ max: aujourdhui() }} value={date} onChange={(e) => setDate(e.target.value)} /></Grid>
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Production des « en maturation »" InputLabelProps={{ shrink: true }}
                inputProps={{ max: aujourdhui() }} value={dateProd} onChange={(e) => setDateProd(e.target.value)} helperText="Vide : date de l'inventaire" /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Commentaire" placeholder="ex. Inventaire du parc avant ERP" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></Grid>
            </Grid>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Type</TableCell><TableCell align="right">Livrables (matures)</TableCell><TableCell align="right">En maturation</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                <TableBody>
                  {types.map((t) => (
                    <TableRow key={t.code}>
                      <TableCell><b>{t.code}</b>{t.designation ? <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}> · {t.designation}</Typography> : null}</TableCell>
                      <TableCell align="right"><TextField variant="standard" value={q[t.code]?.mature || ''} placeholder="0" sx={{ width: 90 }}
                        inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `${t.code} livrables` }} onChange={(e) => set(t.code, 'mature', e.target.value)} /></TableCell>
                      <TableCell align="right"><TextField variant="standard" value={q[t.code]?.en_maturation || ''} placeholder="0" sx={{ width: 90 }}
                        inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `${t.code} en maturation` }} onChange={(e) => set(t.code, 'en_maturation', e.target.value)} /></TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{n(t.code, 'mature') + n(t.code, 'en_maturation') || '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                    <TableCell>Total</TableCell><TableCell align="right" sx={{ fontFamily: tokens.mono }}>{totMature}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{totMat}</TableCell><TableCell align="right" sx={{ fontFamily: tokens.mono }}>{totMature + totMat}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.5 }}>
              Les numéros de ces poteaux pourront être attribués ensuite dans Production → Identifiants poteaux (lots « STOCK-INITIAL »).
            </Typography>
          </>
        ) : (
          hist.length ? (
            <Table size="small">
              <TableHead><TableRow><TableCell>Inventaire du</TableCell><TableCell>Détail</TableCell><TableCell align="right">Total</TableCell><TableCell>Saisi par</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {hist.map((h) => (
                  <TableRow key={h.reference}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(h.date)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{h.lignes.map((l) => `${l.pba_type} ${l.quantite}`).join(' · ')}
                      {h.commentaire && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{h.commentaire}</Typography>}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{h.total}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{h.saisi_par || '—'}<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{dateHeureFr(h.saisi_le)}</Typography></TableCell>
                    <TableCell>{h.annulable ? <Button size="small" color="error" onClick={() => annuler(h)}>Annuler</Button> : <Chip size="small" variant="outlined" label="déjà entamé" />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <Typography sx={{ color: 'text.secondary' }}>Aucune reprise de stock initial.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        {onglet === 0 && <Button variant="contained" disabled={busy || !date || totMature + totMat === 0} onClick={enregistrer}>Enregistrer {totMature + totMat ? `(${totMature + totMat} poteaux)` : ''}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default StockInitialDialog;
